import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import LocalVideoPlayer from './VideoPlayer';
import { 
  FaPlay, 
  FaDownload, 
  FaBook, 
  FaVideo, 
  FaFilePdf, 
  FaFilePowerpoint,
  FaCog,
  FaShieldAlt,
  FaNetworkWired,
  FaServer
} from 'react-icons/fa';
import { ink, inkMid, sky, paper, paperMuted, borderInk, borderInkHover, bodyMuted } from '../theme';
import BlueprintSectionTitle from './BlueprintSectionTitle';

const DocumentationSection = styled.section`
  padding: 100px 0;
  background: ${paperMuted};
  position: relative;
  font-family: 'Inter', system-ui, sans-serif;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const SectionSubtitle = styled.p`
  font-size: 1.05rem;
  text-align: center;
  margin-bottom: 3rem;
  color: ${bodyMuted};
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2.5rem;
    padding: 0 10px;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin-bottom: 2rem;
    padding: 0 5px;
  }

  @media (max-width: 360px) {
    font-size: 0.9rem;
    margin-bottom: 1.75rem;
    line-height: 1.5;
  }
`;

const StickyTabsRail = styled.div`
  position: sticky;
  top: 80px;
  z-index: 200;
  width: calc(100% + 40px);
  max-width: none;
  margin-left: -20px;
  margin-right: -20px;
  margin-bottom: 2.5rem;
  box-sizing: border-box;
  padding: 0.65rem 20px 0.85rem;
  background: ${paperMuted};
  border-bottom: 1px solid ${borderInk};
  box-shadow: 0 6px 18px rgba(3, 26, 56, 0.06);

  @media (max-width: 768px) {
    margin-bottom: 2rem;
    padding: 0.55rem 16px 0.75rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1.75rem;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 10px;
  }

  @media (max-width: 480px) {
    gap: 8px;
    flex-direction: column;
    align-items: center;
  }

  @media (max-width: 360px) {
    gap: 6px;
  }
`;

const Tab = styled.button`
  background: ${props => (props.active ? ink : paper)};
  border: 1px solid ${props => (props.active ? 'rgba(142, 207, 255, 0.45)' : borderInk)};
  border-left: 3px solid ${props => (props.active ? sky : borderInk)};
  color: ${props => (props.active ? '#f8fbff' : bodyMuted)};
  padding: 12px 25px;
  cursor: pointer;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease,
    box-shadow 0.2s ease;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: ${props => (props.active ? '0 6px 20px rgba(3, 26, 56, 0.18)' : 'none')};
  border-radius: 2px;

  svg {
    color: ${props => (props.active ? sky : inkMid)};
  }

  &:hover {
    background: ${props => (props.active ? ink : paperMuted)};
    border-color: ${borderInkHover};
    color: ${props => (props.active ? '#fff' : ink)};
    box-shadow: 0 4px 14px rgba(3, 26, 56, 0.08);
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 0.78rem;
  }

  @media (max-width: 480px) {
    padding: 10px 18px;
    font-size: 0.75rem;
    width: 100%;
    max-width: 280px;
    justify-content: center;
  }

  @media (max-width: 360px) {
    padding: 8px 15px;
    font-size: 0.72rem;
    max-width: 260px;
  }
`;

const ContentArea = styled(motion.div)`
  min-height: 500px;
`;

// ===== ОПИСАНИЕ СИСТЕМЫ =====
const SystemDescription = styled.div`
  background: ${paper};
  border: 1px solid ${borderInk};
  border-radius: 2px;
  padding: 40px;
  margin-bottom: 40px;
  font-family: 'Inter', system-ui, sans-serif;
  position: relative;
  box-shadow: 0 4px 16px rgba(3, 26, 56, 0.05);

  @media (max-width: 768px) {
    padding: 30px 25px;
    margin-bottom: 30px;
  }

  @media (max-width: 480px) {
    padding: 25px 20px;
    margin-bottom: 25px;
  }

  @media (max-width: 360px) {
    padding: 20px 15px;
    margin-bottom: 20px;
  }
`;

const DescriptionHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }

  @media (max-width: 480px) {
    margin-bottom: 25px;
  }

  @media (max-width: 360px) {
    margin-bottom: 20px;
  }
`;

const DescriptionTitle = styled.h3`
  font-size: 2.2rem;
  color: ${inkMid};
  margin-bottom: 15px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 1.9rem;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 1.7rem;
    margin-bottom: 10px;
  }

  @media (max-width: 360px) {
    font-size: 1.5rem;
    margin-bottom: 8px;
  }
`;

const DescriptionSubtitle = styled.p`
  font-size: 1.1rem;
  color: ${bodyMuted};
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 10px;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    padding: 0 5px;
  }

  @media (max-width: 360px) {
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

const DescriptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 25px;
    margin-top: 30px;
  }

  @media (max-width: 480px) {
    gap: 20px;
    margin-top: 25px;
  }

  @media (max-width: 360px) {
    gap: 15px;
    margin-top: 20px;
  }
`;

const DescriptionCard = styled(motion.div)`
  background: ${paperMuted};
  border-radius: 2px;
  padding: 30px;
  border: 1px solid ${borderInk};
  transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: ${borderInkHover};
    box-shadow: 0 8px 22px rgba(3, 26, 56, 0.08);
    transform: translateY(-3px);
  }

  @media (max-width: 768px) {
    padding: 25px 20px;
  }

  @media (max-width: 480px) {
    padding: 20px 15px;
  }

  @media (max-width: 360px) {
    padding: 18px 12px;
  }
`;

const DescriptionIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(45deg, ${sky}, ${inkMid});
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 1.5rem;
  color: #fff;
  box-shadow: 0 6px 20px rgba(3, 26, 56, 0.15);

  @media (max-width: 768px) {
    width: 55px;
    height: 55px;
    font-size: 1.4rem;
    margin-bottom: 18px;
  }

  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
    font-size: 1.3rem;
    margin-bottom: 15px;
  }

  @media (max-width: 360px) {
    width: 45px;
    height: 45px;
    font-size: 1.2rem;
    margin-bottom: 12px;
  }
`;

const DescriptionCardTitle = styled.h4`
  font-size: 1.3rem;
  color: ${ink};
  margin-bottom: 15px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-bottom: 10px;
  }

  @media (max-width: 360px) {
    font-size: 1rem;
    margin-bottom: 8px;
  }
`;

const DescriptionCardText = styled.p`
  color: ${bodyMuted};
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.5;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.4;
  }

  @media (max-width: 360px) {
    font-size: 0.85rem;
    line-height: 1.3;
  }
`;

// ===== PDF ДОКУМЕНТЫ =====
const PdfSection = styled.div`
  background: ${paper};
  border: 1px solid ${borderInk};
  border-left: 3px solid ${sky};
  padding: 40px;
  font-family: 'Inter', system-ui, sans-serif;
  position: relative;

  &::before {
    content: 'Техническая документация';
    position: absolute;
    top: -10px;
    left: 15px;
    background: ${paper};
    padding: 0 10px;
    font-size: 0.7rem;
    color: ${inkMid};
    letter-spacing: 1px;
  }

  @media (max-width: 768px) {
    padding: 30px 25px;
  }

  @media (max-width: 480px) {
    padding: 25px 20px;
  }

  @media (max-width: 360px) {
    padding: 20px 15px;
  }
`;

const PdfHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }

  @media (max-width: 480px) {
    margin-bottom: 25px;
  }

  @media (max-width: 360px) {
    margin-bottom: 20px;
  }
`;

const PdfTitle = styled.h3`
  font-size: 2rem;
  color: ${inkMid};
  margin-bottom: 15px;
  font-weight: 600;
  font-family: 'Inter', system-ui, sans-serif;
  text-transform: uppercase;
  letter-spacing: 2px;

  @media (max-width: 768px) {
    font-size: 1.7rem;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
    margin-bottom: 10px;
  }

  @media (max-width: 360px) {
    font-size: 1.3rem;
    margin-bottom: 8px;
  }
`;

const PdfSubtitle = styled.p`
  font-size: 0.95rem;
  color: ${bodyMuted};
  max-width: 600px;
  margin: 0 auto;
  font-family: 'Inter', system-ui, sans-serif;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0 10px;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 0 5px;
  }

  @media (max-width: 360px) {
    font-size: 0.8rem;
    line-height: 1.4;
  }
`;

const PdfGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 25px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  @media (max-width: 480px) {
    gap: 18px;
  }

  @media (max-width: 360px) {
    gap: 15px;
  }
`;

const PdfCard = styled(motion.div)`
  background: ${paper};
  border: 1px solid ${borderInk};
  border-left: 3px solid ${sky};
  padding: 25px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 20px;
  font-family: 'Inter', system-ui, sans-serif;
  position: relative;

  &::before {
    content: 'DOC';
    position: absolute;
    top: -10px;
    left: 15px;
    background: ${paper};
    padding: 0 10px;
    font-size: 0.7rem;
    color: ${inkMid};
    letter-spacing: 1px;
  }

  &:hover {
    background: ${paperMuted};
    border-color: ${borderInkHover};
    box-shadow: 0 8px 22px rgba(3, 26, 56, 0.08);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 20px 15px;
    gap: 18px;
  }

  @media (max-width: 480px) {
    padding: 18px 12px;
    gap: 15px;
    flex-direction: column;
    text-align: center;
  }

  @media (max-width: 360px) {
    padding: 15px 10px;
    gap: 12px;
  }
`;

const PdfIcon = styled.div`
  width: 60px;
  height: 60px;
  background: rgba(142, 207, 255, 0.15);
  border: 1px solid ${borderInk};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: ${inkMid};
  flex-shrink: 0;
  font-family: 'Inter', system-ui, sans-serif;

  @media (max-width: 768px) {
    width: 55px;
    height: 55px;
    font-size: 1.4rem;
  }

  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
    font-size: 1.3rem;
  }

  @media (max-width: 360px) {
    width: 45px;
    height: 45px;
    font-size: 1.2rem;
  }
`;

const PdfInfo = styled.div`
  flex: 1;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const PdfCardTitle = styled.h4`
  font-size: 1.1rem;
  color: ${ink};
  margin-bottom: 8px;
  font-weight: 600;
  font-family: 'Inter', system-ui, sans-serif;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 6px;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin-bottom: 5px;
  }

  @media (max-width: 360px) {
    font-size: 0.9rem;
    margin-bottom: 4px;
  }
`;

const PdfCardDescription = styled.p`
  color: ${bodyMuted};
  font-size: 0.85rem;
  margin-bottom: 15px;
  line-height: 1.5;
  font-family: 'Inter', system-ui, sans-serif;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    margin-bottom: 10px;
  }

  @media (max-width: 360px) {
    font-size: 0.7rem;
    margin-bottom: 8px;
  }
`;

const PdfCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  font-size: 0.85rem;
  color: ${bodyMuted};

  @media (max-width: 768px) {
    font-size: 0.8rem;
    gap: 12px;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    gap: 10px;
    justify-content: center;
  }

  @media (max-width: 360px) {
    font-size: 0.7rem;
    gap: 8px;
  }
`;

const DownloadButton = styled.button`
  background: ${ink};
  color: #f8fbff;
  border: 1px solid rgba(142, 207, 255, 0.35);
  border-left: 3px solid ${sky};
  padding: 10px 18px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 700;
  font-family: 'Inter', system-ui, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 14px rgba(3, 26, 56, 0.12);

  &:hover {
    background: ${inkMid};
    border-color: rgba(142, 207, 255, 0.5);
    box-shadow: 0 8px 22px rgba(3, 26, 56, 0.18);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 8px 15px;
    font-size: 0.8rem;
  }

  @media (max-width: 480px) {
    padding: 8px 14px;
    font-size: 0.75rem;
    width: 100%;
    justify-content: center;
  }

  @media (max-width: 360px) {
    padding: 6px 12px;
    font-size: 0.7rem;
  }
`;

// ===== ВИДЕО УРОКИ =====
const VideoSection = styled.div`
  background: ${paper};
  border: 1px solid ${borderInk};
  border-left: 3px solid ${sky};
  padding: 40px;
  font-family: 'Inter', system-ui, sans-serif;
  position: relative;

  &::before {
    content: 'Подкасты';
    position: absolute;
    top: -10px;
    left: 15px;
    background: ${paper};
    padding: 0 10px;
    font-size: 0.7rem;
    color: ${inkMid};
    letter-spacing: 1px;
  }

  @media (max-width: 768px) {
    padding: 30px 25px;
  }

  @media (max-width: 480px) {
    padding: 25px 20px;
  }

  @media (max-width: 360px) {
    padding: 20px 15px;
  }
`;

const VideoHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }

  @media (max-width: 480px) {
    margin-bottom: 25px;
  }

  @media (max-width: 360px) {
    margin-bottom: 20px;
  }
`;

const VideoTitle = styled.h3`
  font-size: 2rem;
  color: ${inkMid};
  margin-bottom: 15px;
  font-weight: 700;
  font-family: 'Inter', system-ui, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.08em;

  @media (max-width: 768px) {
    font-size: 1.7rem;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
    margin-bottom: 10px;
  }

  @media (max-width: 360px) {
    font-size: 1.3rem;
    margin-bottom: 8px;
  }
`;

const VideoSubtitle = styled.p`
  font-size: 0.95rem;
  color: ${bodyMuted};
  max-width: 600px;
  margin: 0 auto;
  font-family: 'Inter', system-ui, sans-serif;
  letter-spacing: 0.02em;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0 10px;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 0 5px;
  }

  @media (max-width: 360px) {
    font-size: 0.8rem;
    line-height: 1.4;
  }
`;

const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 25px;
  }

  @media (max-width: 480px) {
    gap: 20px;
  }

  @media (max-width: 360px) {
    gap: 15px;
  }
`;

const VideoCard = styled(motion.div)`
  background: ${paper};
  border: 1px solid ${borderInk};
  border-left: 3px solid ${sky};
  overflow: hidden;
  transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
  font-family: 'Inter', system-ui, sans-serif;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: 15px;
    background: ${paper};
    padding: 0 10px;
    font-size: 0.7rem;
    color: ${inkMid};
    letter-spacing: 1px;
    z-index: 1;
  }

  &:hover {
    background: ${paper};
    border-color: ${borderInkHover};
    box-shadow: 0 8px 22px rgba(3, 26, 56, 0.08);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    border-radius: 0;
  }

  @media (max-width: 480px) {
    border-radius: 0;
  }

  @media (max-width: 360px) {
    border-radius: 0;
  }
`;

const VideoThumbnail = styled.div`
  position: relative;
  height: 200px;
  background: ${ink};
  border-bottom: 1px solid ${borderInk};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
  font-family: 'Inter', system-ui, sans-serif;

  &:hover {
    background: ${inkMid};
    border-bottom-color: rgba(142, 207, 255, 0.35);
  }

  @media (max-width: 768px) {
    height: 180px;
  }

  @media (max-width: 480px) {
    height: 160px;
  }

  @media (max-width: 360px) {
    height: 140px;
  }
`;

const PlayButton = styled.div`
  width: 60px;
  height: 60px;
  background: rgba(142, 207, 255, 0.12);
  border: 2px solid rgba(142, 207, 255, 0.55);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: ${sky};
  transition: transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
  font-family: 'Inter', system-ui, sans-serif;
  box-shadow: 0 4px 20px rgba(3, 26, 56, 0.25);

  &:hover {
    transform: scale(1.08);
    background: rgba(142, 207, 255, 0.2);
    box-shadow: 0 6px 28px rgba(3, 26, 56, 0.3);
  }

  @media (max-width: 768px) {
    width: 55px;
    height: 55px;
    font-size: 1.4rem;
  }

  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
    font-size: 1.3rem;
  }

  @media (max-width: 360px) {
    width: 45px;
    height: 45px;
    font-size: 1.2rem;
  }
`;

const VideoInfo = styled.div`
  padding: 25px;
  font-family: 'Inter', system-ui, sans-serif;

  @media (max-width: 768px) {
    padding: 20px;
  }

  @media (max-width: 480px) {
    padding: 18px;
  }

  @media (max-width: 360px) {
    padding: 15px;
  }
`;

const VideoCardTitle = styled.h4`
  font-size: 1.1rem;
  color: ${ink};
  margin-bottom: 10px;
  font-weight: 600;
  font-family: 'Inter', system-ui, sans-serif;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin-bottom: 6px;
  }

  @media (max-width: 360px) {
    font-size: 0.9rem;
    margin-bottom: 5px;
  }
`;

const VideoCardDescription = styled.p`
  color: ${bodyMuted};
  font-size: 0.85rem;
  line-height: 1.5;
  margin-bottom: 15px;
  font-family: 'Inter', system-ui, sans-serif;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    margin-bottom: 10px;
  }

  @media (max-width: 360px) {
    font-size: 0.7rem;
    margin-bottom: 8px;
  }
`;

const VideoCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  font-size: 0.8rem;
  color: ${bodyMuted};
  font-family: 'Inter', system-ui, sans-serif;
  letter-spacing: 0.04em;

  @media (max-width: 768px) {
    font-size: 0.75rem;
    gap: 12px;
  }

  @media (max-width: 480px) {
    font-size: 0.7rem;
    gap: 10px;
  }

  @media (max-width: 360px) {
    font-size: 0.65rem;
    gap: 8px;
  }
`;

// ===== МОДАЛЬНОЕ ОКНО =====
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 15px;
  }

  @media (max-width: 480px) {
    padding: 10px;
  }

  @media (max-width: 360px) {
    padding: 5px;
  }
`;

const ModalContent = styled.div`
  background: ${paper};
  border-radius: 4px;
  padding: 30px;
  max-width: 800px;
  width: 100%;
  border: 1px solid ${borderInk};
  box-shadow: 0 20px 60px rgba(3, 26, 56, 0.35);

  @media (max-width: 768px) {
    padding: 25px;
    border-radius: 15px;
  }

  @media (max-width: 480px) {
    padding: 20px;
    border-radius: 12px;
  }

  @media (max-width: 360px) {
    padding: 15px;
    border-radius: 10px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    margin-bottom: 15px;
  }

  @media (max-width: 480px) {
    margin-bottom: 12px;
  }

  @media (max-width: 360px) {
    margin-bottom: 10px;
  }
`;

const ModalTitle = styled.h3`
  color: ${ink};
  font-size: 1.5rem;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }

  @media (max-width: 360px) {
    font-size: 1.1rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${ink};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px;
  border-radius: 5px;
  transition: background 0.2s ease;

  &:hover {
    background: ${paperMuted};
  }

  @media (max-width: 768px) {
    font-size: 1.4rem;
    padding: 4px;
  }

  @media (max-width: 480px) {
    font-size: 1.3rem;
    padding: 3px;
  }

  @media (max-width: 360px) {
    font-size: 1.2rem;
    padding: 2px;
  }
`;

const VideoPlayer = styled.div`
  width: 100%;
  height: 470px;
  background: #0a0f18;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.88);
  font-size: 1.2rem;

  @media (max-width: 768px) {
    height: 300px;
    font-size: 1.1rem;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    height: 250px;
    font-size: 1rem;
    border-radius: 6px;
  }

  @media (max-width: 360px) {
    height: 200px;
    font-size: 0.9rem;
    border-radius: 5px;
  }
`;

const Documentation = () => {
  const [activeTab, setActiveTab] = useState('description');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const systemDescription = [
    {
      icon: <FaServer />,
      title: "Распределенная архитектура",
      description: "e-node оазис (cloud) построен на распределенной архитектуре, обеспечивающей масштабируемость за счет распределения нагрузки между узлами и автономную работу агентов."
    },
    {
      icon: <FaShieldAlt />,
      title: "Интеграция с платформами",
      description: "Поддержка VMware, Proxmox, zVirt, Базис, Docker, Kubernetes и других платформ виртуализации и контейнеризации."
    },
    {
      icon: <FaCog />,
      title: "Автоматизация управления",
      description: "Автоматическая генерация правил безопасности на основе политик, связей и контекста для упрощения управления."
    },
    {
      icon: <FaNetworkWired />,
      title: "Централизованное управление",
      description: "Единый интерфейс для настройки и мониторинга всей инфраструктуры безопасности с распределенными функциями МСЭ."
    }
  ];

  const pdfDocuments = [
    {
      icon: <FaFilePowerpoint />,
      title: "Описание системы e-node оазис (cloud)",
      description: "Презентация системы e-node оазис (cloud)",
      size: "1.0 MB",
      pages: "10 стр.",
      version: "v1.0",
      filename: "e-node-cloud.pdf"
    },
    {
      icon: <FaFilePdf />,
      title: "Функции и задачи e-node оазис (cloud)",
      description: "Описание основных задач, решаемых системой, Класиификация рисков ИБ Заказчиков",
      size: "1.2 MB",
      pages: "12 стр.",
      version: "v0.8",
      filename: "e-node-tech.pdf"
    },
    {
      icon: <FaFilePdf />,
      title: "e-node оазис (cloud) как инспектор безопасности",
      description: "Описание функций системы как инспектора безопасности",
      size: "0.2 MB",
      pages: "4 стр.",
      version: "v0.6",
      filename: "e-node-inspector.pdf"
    },
    // {
    //   icon: <FaFileExcel />,
    //   title: "Сравнительная таблица решений",
    //   description: "Детальное сравнение e-node оазис (cloud) с традиционными решениями сетевой безопасности",
    //   size: "0.8 MB",
    //   pages: "12 стр.",
    //   version: "v2.1"
    // }
  ];

  const videoTutorials = [
    {
      title: "Пример защищаемой сетевой инфраструктуры",
      description: "Готовимся производить сегментацию сложных информационных систем",
      duration: "06:25",
      render: <LocalVideoPlayer 
        src="/video/1.mp4" 
        title="Быстрый старт системы" 
        width="720" 
        height="405" 
        controls={true}
        autoplay={false}
      />,
      enable: true
    },
    {
      title: "Интеграция с платформами виртуализации",
      description: "Настройка интеграции с VMware, Proxmox и другими гипервизорами",
      duration: "22:18",
      level: "Средний",
      views: "1,856"
    },
    {
      title: "Настройка политик безопасности",
      description: "Создание и управление политиками безопасности для различных типов объектов",
      duration: "28:42",
      level: "Средний",
      views: "987"
    },
    {
      title: "Работа с микросервисами",
      description: "Настройка безопасности для контейнеров Docker и Kubernetes",
      duration: "35:15",
      level: "Продвинутый",
      views: "654"
    },
    {
      title: "Интеграция с SIEM-системами",
      description: "Настройка передачи данных в внешние системы анализа угроз",
      duration: "18:33",
      level: "Продвинутый",
      views: "432"
    },
    {
      title: "Мониторинг и аналитика",
      description: "Настройка дашбордов и создание пользовательских отчетов",
      duration: "25:47",
      level: "Средний",
      views: "789"
    }
  ];

  const openVideo = (video) => {
    setSelectedVideo(video);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedVideo(null);
  };

  const downloadDocument = (doc) => {
    if (!doc.filename) {
      console.error('Файл не найден для документа:', doc.title);
      return;
    }

    // Создаем ссылку для скачивания
    const link = document.createElement('a');
    link.href = `/doc/${doc.filename}`;
    link.download = doc.filename;
    link.target = '_blank';
    
    // Добавляем ссылку в DOM, кликаем и удаляем
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log(`Скачивание: ${doc.title} (${doc.filename})`);
  };

  return (
    <DocumentationSection id="documentation">
      <Container>
        <BlueprintSectionTitle>Документация и обучение</BlueprintSectionTitle>
        <SectionSubtitle>
          Получите доступ к подробной технической документации, руководствам по развертыванию 
          и обучающим видео для эффективного использования e-node оазис (cloud)
        </SectionSubtitle>

        <StickyTabsRail>
          <TabsContainer>
            <Tab
              active={activeTab === 'description'}
              onClick={() => setActiveTab('description')}
            >
              <FaBook /> Описание системы
            </Tab>
            <Tab
              active={activeTab === 'pdf'}
              onClick={() => setActiveTab('pdf')}
            >
              <FaFilePdf /> Техническая документация
            </Tab>
            <Tab
              active={activeTab === 'video'}
              onClick={() => setActiveTab('video')}
            >
              <FaVideo /> Видео подкасты
            </Tab>
          </TabsContainer>
        </StickyTabsRail>

        <ContentArea
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {activeTab === 'description' && (
            <SystemDescription>
              <DescriptionHeader>
                <DescriptionTitle>Описание системы e-node оазис (cloud)</DescriptionTitle>
                <DescriptionSubtitle>
                  Распределенная система контроля и управления сетевой безопасностью 
                  технологических и бизнес-процессов компаний
                </DescriptionSubtitle>
              </DescriptionHeader>
              
              <DescriptionGrid>
                {systemDescription.map((item, index) => (
                  <DescriptionCard
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <DescriptionIcon>{item.icon}</DescriptionIcon>
                    <DescriptionCardTitle>{item.title}</DescriptionCardTitle>
                    <DescriptionCardText>{item.description}</DescriptionCardText>
                  </DescriptionCard>
                ))}
              </DescriptionGrid>
            </SystemDescription>
          )}

          {activeTab === 'pdf' && (
            <PdfSection>
              <PdfHeader>
                <PdfTitle>Техническая документация</PdfTitle>
                <PdfSubtitle>
                  Скачайте подробную техническую документацию, руководства по развертыванию 
                  и презентации для глубокого изучения системы
                </PdfSubtitle>
              </PdfHeader>
              
              <PdfGrid>
                {pdfDocuments.map((doc, index) => (
                  <PdfCard
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <PdfIcon>{doc.icon}</PdfIcon>
                    <PdfInfo>
                      <PdfCardTitle>{doc.title}</PdfCardTitle>
                      <PdfCardDescription>{doc.description}</PdfCardDescription>
                      <PdfCardMeta>
                        <span>{doc.size}</span>
                        <span>•</span>
                        <span>{doc.pages}</span>
                        <span>•</span>
                        <span>{doc.version}</span>
                      </PdfCardMeta>
                    </PdfInfo>
                    <DownloadButton onClick={() => downloadDocument(doc)}>
                      <FaDownload /> Скачать
                    </DownloadButton>
                  </PdfCard>
                ))}
              </PdfGrid>
            </PdfSection>
          )}

          {activeTab === 'video' && (
            <VideoSection>
              <VideoHeader>
                <VideoTitle>Видео подкасты</VideoTitle>
                <VideoSubtitle>
                  Обучающие видео помогут быстро освоить все возможности системы 
                  и эффективно использовать e-node оазис (cloud) в работе
                </VideoSubtitle>
              </VideoHeader>
              
              <VideoGrid>
                {videoTutorials.map((video, index) => (
                  <VideoCard
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >

                  {video.enable && ( 
                    <>
                      <VideoThumbnail onClick={() => openVideo(video)}>
                        <PlayButton>
                          <FaPlay />
                        </PlayButton>
                      </VideoThumbnail>
                      <VideoInfo>
                        <VideoCardTitle>{video.title}</VideoCardTitle>
                        <VideoCardDescription>{video.description}</VideoCardDescription>
                        <VideoCardMeta>
                          <span>⏱ {video.duration}</span>
                          <span>•</span>
                        </VideoCardMeta>
                      </VideoInfo>
                    </>
                  )}
                  </VideoCard>
                ))}
              </VideoGrid>
            </VideoSection>
          )}
        </ContentArea>
      </Container>

      {isModalOpen && selectedVideo && (
        <Modal onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{selectedVideo.title}</ModalTitle>
              <CloseButton onClick={closeModal}>×</CloseButton>
            </ModalHeader>
            <VideoPlayer>
              <div>
                <div style={{ marginBottom: '20px' }}>🎥 Видео: {selectedVideo.title}</div>
                <div style={{ fontSize: '1rem', opacity: 0.8 }}>
                  {selectedVideo.render}
                </div>
              </div>
            </VideoPlayer>
          </ModalContent>
        </Modal>
      )}
    </DocumentationSection>
  );
};

export default Documentation;
